<template>
  <div class="container-fluid mt-5 pt-5">
    <div class="row">
      <div class="col-12">
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4 class="mb-0">
              <i class="fas fa-edit me-2"></i>
              Edit Blog
            </h4>
            <nav aria-label="breadcrumb">
              <ol class="breadcrumb mb-0">
                <li class="breadcrumb-item">
                  <router-link to="/pms/admin/dashboard">Dashboard</router-link>
                </li>
                <li class="breadcrumb-item">
                  <router-link to="/blogs">Blogs</router-link>
                </li>
                <li class="breadcrumb-item active">Edit</li>
              </ol>
            </nav>
          </div>
          <div class="d-flex gap-2">
            <button class="btn btn-outline-secondary" @click="goBack">
              <i class="fas fa-arrow-left me-1"></i>
              Back to List
            </button>
            <button
              class="btn btn-success"
              @click="saveBlog"
              :disabled="loading"
            >
              <span
                v-if="loading"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              <i v-else class="fas fa-save me-1"></i>
              Save Changes
            </button>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loadingBlog" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <div class="mt-2">Loading blog...</div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="alert alert-danger">
          <i class="fas fa-exclamation-triangle me-2"></i>
          {{ error }}
        </div>

        <!-- Blog Form -->
        <div v-else-if="blog" class="card">
          <div class="card-body">
            <BlogForm
              ref="formRef"
              :blog="blog"
              :categories="categories"
              @save="handleSave"
              @cancel="goBack"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import apiService from "@/services/apiService";
import BlogForm from "@/components/pms/admin/BlogForm.vue";

// Router
const router = useRouter();
const route = useRoute();

// State
const formRef = ref<any>(null);
const loading = ref(false);
const loadingBlog = ref(false);
const blog = ref(null);
const categories = ref([]);
const error = ref("");

// Methods
const fetchBlog = async () => {
  try {
    loadingBlog.value = true;
    error.value = "";
    const blogId = route.params.id as string;

    if (!blogId) {
      error.value = "Blog ID is required";
      return;
    }

    const response = await apiService.getKnowledgeById(blogId);
    blog.value = response;
  } catch (err: any) {
    console.error("Error fetching blog:", err);
    error.value = err.response?.data?.message || "Failed to load blog";
  } finally {
    loadingBlog.value = false;
  }
};

const fetchCategories = async () => {
  try {
    const response = await apiService.getKnowledgeCategories({
      is_active: true,
    });
    categories.value = response.data || [];
  } catch (error) {
    console.error("Error fetching categories:", error);
  }
};

const handleSave = async (blogData: any) => {
  try {
    loading.value = true;
    const blogId = route.params.id as string;

    await apiService.updateKnowledge(blogId, blogData);

    // Show success message or redirect
    router.push("/knowledge");
  } catch (err: any) {
    console.error("Error updating blog:", err);
    alert("Failed to update blog. Please try again.");
  } finally {
    loading.value = false;
  }
};

const saveBlog = () => {
  // Trigger save from the form component
  if (formRef.value) {
    formRef.value.handleSubmit();
  }
};

const goBack = () => {
  router.push("/blogs");
};

// Lifecycle
onMounted(() => {
  fetchCategories();
  fetchBlog();
});
</script>

<style scoped>
.breadcrumb {
  margin-bottom: 0;
  font-size: 0.875rem;
}

.breadcrumb-item + .breadcrumb-item::before {
  content: ">";
}

.card {
  border: 1px solid #dee2e6;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}
</style>
